// ResizeObserver hook: picks full/abbrev/dot project pill tier from hidden measure samples.
import { useLayoutEffect, useState } from 'react';
import { resolveProjectPillTier } from '@/features/tasks/utils/ui/projectPillDisplayTier';
import {
  attachProjectPillTierObservers,
  measureTaskRowPillsColBudgetFromCol,
  readProjectPillMeasureWidths,
} from '@/features/tasks/utils/ui/taskRowProjectPillTierDom';

const PILLS_GAP_PX = 4;

/**
 * ResizeObserver on the pills column; measures hidden tier samples.
 * @param {import('react').RefObject<HTMLElement|null>} pillsColRef
 * @param {import('react').RefObject<HTMLElement|null>} contextPillsRef
 * @param {import('react').RefObject<HTMLElement|null>} measureRef
 * @param {boolean} hasProjectPills
 * @param {string} measureKey — changes when project labels or density change
 */
export function useTaskRowProjectPillTier(
  pillsColRef,
  contextPillsRef,
  measureRef,
  hasProjectPills,
  measureKey,
) {
  const [tier, setTier] = useState('full');

  useLayoutEffect(() => {
    if (!hasProjectPills) {
      setTier('full');
      return undefined;
    }

    const col = pillsColRef.current;
    const measure = measureRef.current;
    if (!col || !measure) return undefined;

    const update = () => {
      const projectWidthsPx = readProjectPillMeasureWidths(measure);
      const next = resolveProjectPillTier({
        pillsColWidthPx: measureTaskRowPillsColBudgetFromCol(col),
        contextPillsWidthPx:
          contextPillsRef.current?.getBoundingClientRect().width ?? 0,
        gapPx: PILLS_GAP_PX,
        projectWidthsPx,
      });
      setTier((prev) => (prev === next ? prev : next));
    };

    update();
    return attachProjectPillTierObservers(col, contextPillsRef, update);
  }, [pillsColRef, contextPillsRef, measureRef, hasProjectPills, measureKey]);

  return tier;
}
